import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ArrowRight, Sparkles, CheckCircle2, Lock, ShieldCheck } from 'lucide-react'
import { selectLoggedInUser, useAppSelector } from '@app.raytd.com/store'
import { isRoleGreaterThanOrEqual } from '@/app/utils/roles'
import { featureFlags } from '@/config/features'

interface UpgradeContextType {
  triggerUpgrade: (featureName?: string) => void
}

const UpgradeModalContext = createContext<UpgradeContextType | undefined>(
  undefined
)

interface UpgradeModalProviderProps {
  children: ReactNode
}

export function UpgradeModalProvider({
  children,
}: UpgradeModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [feature, setFeature] = useState<string | null>(null)
  const loggedInUser = useAppSelector(selectLoggedInUser)

  // Check if user is admin
  const isAdmin = loggedInUser?.company?.role
    ? isRoleGreaterThanOrEqual(
      loggedInUser.company.role as 'admin' | 'member' | 'guest',
      'admin'
    )
    : false

  const triggerUpgrade = useCallback((featureName?: string) => {
    setFeature(featureName || 'Premium Feature')
    setIsOpen(true)
  }, [])

  // When billing is disabled there is no self-serve purchase flow, so the
  // primary CTA contacts us instead of routing into the (hidden) billing page.
  const billingEnabled = featureFlags.billingEnabled()

  const handleContactSales = useCallback(() => {
    window.location.href = 'mailto:sales@raytd.com?subject=Enterprise%20Upgrade%20Inquiry'
  }, [])

  const handleUpgradeClick = useCallback(() => {
    setIsOpen(false)
    if (!billingEnabled) {
      handleContactSales()
      return
    }
    window.location.href = '/organisation/billing'
  }, [billingEnabled, handleContactSales])

  const handleRequestAccess = useCallback(() => {
    // In a real app, this would trigger a notification to the admin.
    // For now, we open a mailto link to the admin if available, or generic.
    const subject = `Request for Pro Access: ${feature}`
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`I need access to ${feature} which requires a Pro plan upgrade.`)}`
    setIsOpen(false)
  }, [feature])

  const displayFeature = feature || 'Premium Capabilities'

  return (
    <UpgradeModalContext.Provider value={{ triggerUpgrade }}>
      {children}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="p-0 sm:max-w-[480px] overflow-hidden gap-0 border-none shadow-2xl">

          {/* Hero Section */}
          <div className="bg-zinc-900 p-8 text-center relative overflow-hidden">
            {/* Decorative background blur - muted zinc */}
            <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-zinc-600 via-zinc-700 to-transparent pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center">
              <div className="h-12 w-12 bg-zinc-800 rounded-xl flex items-center justify-center shadow-lg mb-4 ring-4 ring-white/5 border border-zinc-700">
                <Lock className="h-6 w-6 text-zinc-300" />
              </div>
              <DialogTitle className="text-xl font-semibold text-white mb-2">
                Unlock {displayFeature}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 max-w-xs mx-auto text-center">
                This feature is available exclusively on our <strong>Pro Plan</strong>.
              </DialogDescription>
            </div>
          </div>

          {/* Content Body */}
          <div className="p-6 bg-white">
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-zinc-900 uppercase tracking-wider">
                Included in Pro
              </h4>
              <ul className="grid gap-3">
                {[
                  'Unlimited reports and assets',
                  'Unlimited inspections',
                  'Custom Test Suites',
                  'Priority Enterprise support',
                  'Mobile inspection app'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-600">
                    <CheckCircle2 className="h-5 w-5 text-zinc-500 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 bg-zinc-50 border-t border-zinc-100 flex flex-col gap-3">
            {isAdmin ? (
              <>
                <Button
                  size="lg"
                  className="w-full bg-zinc-900 hover:bg-zinc-800 text-white gap-2 shadow-lg shadow-zinc-900/10"
                  onClick={handleUpgradeClick}
                >
                  <Sparkles className="h-4 w-4 text-zinc-300" />
                  {billingEnabled ? 'Upgrade to Pro' : 'Contact us'}
                </Button>
                <div className="flex items-center justify-center gap-2 pt-2">
                  {billingEnabled && (
                    <>
                      <button onClick={handleContactSales} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium underline underline-offset-4">
                        Contact Sales
                      </button>
                      <span className="text-zinc-300">•</span>
                    </>
                  )}
                  <button onClick={() => setIsOpen(false)} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium">
                    No thanks
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-md bg-zinc-100 border border-zinc-200 p-3 flex gap-3 items-start">
                  <ShieldCheck className="h-5 w-5 text-zinc-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-zinc-700">
                    You need administrator permissions to upgrade the billing plan for this workspace.
                  </p>
                </div>
                <Button variant="outline" className="w-full" onClick={handleRequestAccess}>
                  Request Access from Admin
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </UpgradeModalContext.Provider>
  )
}

export function useUpgradeModal() {
  const context = useContext(UpgradeModalContext)
  if (!context) {
    throw new Error(
      'useUpgradeModal must be used within UpgradeModalProvider'
    )
  }
  return context
}

